import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "../api";
import { Modal } from "../components/ui";
import DateIT from "../components/DateIT";
import Autocomplete from "../components/Autocomplete";

const today = () => new Date().toISOString().slice(0, 10);
const isLead = (p) => p.status === "lead";
const isLost = (p) => p.status === "perso";
const isExpired = (p) => (!!p.end_date && p.end_date < today()) || isLost(p);
const fmtIt = (iso) => {
  if (!iso) return "";
  const m = String(iso).slice(0, 10).split("-");
  return m.length === 3 ? `${m[2]}/${m[1]}/${m[0]}` : "";
};

export default function Projects() {
  const [list, setList] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [q, setQ] = useState("");
  const [view, setView] = useState("validi"); // validi | scaduti | lead | tutti
  const [modal, setModal] = useState(null); // {id?, company_id, name, end_date, kind}
  const [acquire, setAcquire] = useState(null); // {project, company_id, company_name}
  const [merge, setMerge] = useState(null); // {primary_id, duplicate_id}

  const load = useCallback(() => {
    api.get("/projects").then((r) => setList(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  const loadCompanies = () => api.get("/companies").then((r) => setCompanies(r.data)).catch(() => {});
  useEffect(() => { loadCompanies(); }, []);

  const cname = (id) => companies.find((c) => c.id === id)?.name || "—";

  const save = async () => {
    if (!modal.name.trim()) { alert("Indica un nome"); return; }
    const lead = modal.kind === "lead";
    if (!lead && !modal.company_id) { alert("Seleziona un'azienda"); return; }
    try {
      const body = { name: modal.name.trim(), end_date: modal.end_date || "" };
      if (modal.id) {
        await api.put(`/projects/${modal.id}`, body);
      } else {
        await api.post("/projects", { company_id: modal.company_id || null, status: lead ? "lead" : "attivo", ...body });
      }
      setModal(null); load();
    } catch (e) { alert("Errore: " + (e.response?.data?.detail || e.message)); }
  };
  const remove = async (id) => { if (window.confirm("Eliminare il progetto e le sue cartelle?")) { await api.delete(`/projects/${id}`); load(); } };

  // Esito lead
  const markLost = async (p) => {
    if (!window.confirm(`Segnare "${p.name}" come NON acquisito? Verrà reso non valido.`)) return;
    await api.put(`/projects/${p.id}`, { status: "perso", end_date: today() });
    load();
  };
  const confirmAcquire = async () => {
    if (!acquire.company_id) { alert("Seleziona o crea l'azienda cliente"); return; }
    try {
      await api.put(`/projects/${acquire.project.id}`, { status: "attivo", company_id: acquire.company_id });
      setAcquire(null); load();
    } catch (e) { alert("Errore: " + (e.response?.data?.detail || e.message)); }
  };

  const leads = list.filter(isLead);
  const nonLead = list.filter((p) => !isLead(p));
  const valid = nonLead.filter((p) => !isExpired(p));
  const invalid = nonLead.filter(isExpired);

  let base = view === "lead" ? leads : view === "validi" ? valid : view === "scaduti" ? invalid : nonLead;
  const filtered = q ? base.filter((p) => (p.name + " " + cname(p.company_id)).toLowerCase().includes(q.toLowerCase())) : base;

  return (
    <div>
      <h1 className="page-title">Progetti</h1>
      <p className="page-sub">Ogni cliente ha uno o più progetti, con cartelle e documenti</p>

      <div className="toolbar">
        <input style={{ maxWidth: 280 }} placeholder="Cerca progetto o azienda…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="project-search" />
        <div style={{ display: "flex", gap: 6, marginLeft: 12, flexWrap: "wrap" }}>
          <button className={"btn sm" + (view === "validi" ? " primary" : "")} onClick={() => setView("validi")} data-testid="filter-validi">Validi ({valid.length})</button>
          <button className={"btn sm" + (view === "scaduti" ? " primary" : "")} onClick={() => setView("scaduti")} data-testid="filter-scaduti">Non più validi ({invalid.length})</button>
          <button className={"btn sm" + (view === "lead" ? " primary" : "")} onClick={() => setView("lead")} data-testid="filter-lead">🎯 Lead in acquisizione ({leads.length})</button>
          <button className={"btn sm" + (view === "tutti" ? " primary" : "")} onClick={() => setView("tutti")} data-testid="filter-tutti">Tutti ({nonLead.length})</button>
        </div>
        <div className="spacer" />
        <button className="btn" onClick={() => setMerge({ primary_id: "", duplicate_id: "" })} data-testid="project-merge-btn">🔀 Unifica doppioni</button>
        {view === "lead"
          ? <button className="btn primary" onClick={() => setModal({ company_id: "", name: "", end_date: "", kind: "lead" })} data-testid="new-lead-btn">+ Nuovo lead</button>
          : <button className="btn primary" onClick={() => setModal({ company_id: "", name: "", end_date: "", kind: "cliente" })} data-testid="new-project-btn">+ Nuovo progetto</button>}
      </div>

      {view === "lead" && <p className="muted" style={{ marginTop: -6 }}>Lead che stai lavorando. Quando concludi: <b>Acquisito</b> → diventa un progetto cliente · <b>Non acquisito</b> → reso non valido.</p>}

      <div className="panel">
        {filtered.length === 0 ? <p className="empty">{view === "lead" ? "Nessun lead in acquisizione." : "Nessun progetto."}</p> : (
          <table>
            <thead><tr><th>{view === "lead" ? "Lead" : "Progetto"}</th><th>Azienda</th><th>Stato</th><th>{view === "lead" ? "Esito" : "Data fine"}</th><th></th></tr></thead>
            <tbody>
              {filtered.map((p) => {
                const exp = isExpired(p);
                return (
                  <tr key={p.id} data-testid={`project-row-${p.id}`} style={exp ? { opacity: 0.6 } : undefined}>
                    <td><Link to={`/progetti/${p.id}`}>{p.name}</Link></td>
                    <td>{p.company_id ? <Link to={`/aziende/${p.company_id}`}>{cname(p.company_id)}</Link> : <span className="muted">—</span>}</td>
                    <td>
                      {isLead(p) ? <span className="pill" style={{ background: "#1e3a8a", color: "#bfdbfe" }}>in acquisizione</span>
                        : isLost(p) ? <span className="pill" style={{ background: "#7f1d1d", color: "#fecaca" }}>non acquisito</span>
                        : exp ? <span className="pill" style={{ background: "#fde2e2", color: "#a11" }}>scaduto</span>
                        : <span className="pill">{p.status}</span>}
                    </td>
                    <td>
                      {isLead(p)
                        ? <span style={{ whiteSpace: "nowrap" }}>
                            <button className="btn sm" style={{ color: "#6ee7b7", borderColor: "rgba(110,231,183,.4)" }} onClick={() => { loadCompanies(); setAcquire({ project: p, company_id: p.company_id || "", company_name: p.company_id ? cname(p.company_id) : "" }); }} data-testid={`lead-acquire-${p.id}`}>✓ Acquisito</button>{" "}
                            <button className="btn sm danger" onClick={() => markLost(p)} data-testid={`lead-lose-${p.id}`}>✗ Non acquisito</button>
                          </span>
                        : (p.end_date ? fmtIt(p.end_date) : <span className="muted">—</span>)}
                    </td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      <button className="btn sm" onClick={() => setModal({ id: p.id, company_id: p.company_id || "", name: p.name, end_date: p.end_date || "", kind: isLead(p) ? "lead" : "cliente" })} data-testid={`project-edit-${p.id}`}>Modifica</button>{" "}
                      <Link className="btn sm" to={`/progetti/${p.id}`}>Apri</Link>{" "}
                      <button className="btn sm danger" onClick={() => remove(p.id)}>Elimina</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {modal && (
        <Modal title={modal.id ? "Modifica" : (modal.kind === "lead" ? "Nuovo lead in acquisizione" : "Nuovo progetto")} onClose={() => setModal(null)}>
          {modal.kind === "lead" ? (
            <div className="field"><label>Azienda / cliente (facoltativo finché non acquisito)</label>
              <Autocomplete testid="lead-company-ac"
                items={companies.map((c) => ({ id: c.id, label: c.name }))}
                initialText={modal.company_id ? cname(modal.company_id) : ""}
                placeholder="Cerca azienda… (puoi lasciarlo vuoto)"
                createLabel="Crea azienda"
                onSelect={(it) => setModal((m) => ({ ...m, company_id: it.id }))}
                onCreate={async (txt) => { try { const r = await api.post("/companies", { name: txt }); await loadCompanies(); setModal((m) => ({ ...m, company_id: r.data.id })); } catch (e) { alert("Errore: " + (e?.response?.data?.detail || e.message)); } }} />
            </div>
          ) : (
            <div className="field"><label>Azienda *</label>
              <Autocomplete testid="project-company-ac"
                items={companies.map((c) => ({ id: c.id, label: c.name }))}
                initialText={modal.company_id ? cname(modal.company_id) : ""}
                placeholder="Cerca l'azienda…"
                createLabel="Crea azienda"
                onSelect={(it) => setModal((m) => ({ ...m, company_id: it.id }))}
                onCreate={async (txt) => { try { const r = await api.post("/companies", { name: txt }); await loadCompanies(); setModal((m) => ({ ...m, company_id: r.data.id })); } catch (e) { alert("Errore: " + (e?.response?.data?.detail || e.message)); } }} />
            </div>
          )}
          <div className="field"><label>{modal.kind === "lead" ? "Nome lead / opportunità *" : "Nome progetto *"}</label><input value={modal.name} onChange={(e) => setModal({ ...modal, name: e.target.value })} placeholder={modal.kind === "lead" ? "es. Mario Rossi – nuova SRL" : "es. Contabilità 2026"} data-testid="project-name" /></div>
          {modal.kind !== "lead" && (
            <div className="field"><label>Data di fine (opzionale)</label>
              <DateIT value={modal.end_date} onChange={(v) => setModal({ ...modal, end_date: v })} data-testid="project-end-date" />
              <small className="muted">Oltre questa data il progetto non comparirà più tra i "Validi".</small>
            </div>
          )}
          <div className="modal-actions">
            <button className="btn" onClick={() => setModal(null)}>Annulla</button>
            <button className="btn primary" onClick={save} data-testid="project-save">{modal.id ? "Salva" : "Crea"}</button>
          </div>
        </Modal>
      )}

      {acquire && (
        <Modal title="Lead acquisito 🎉" onClose={() => setAcquire(null)}>
          <p className="muted">Indica l'azienda cliente: il lead diventerà un progetto attivo collegato a questo cliente.</p>
          <div className="field"><label>Azienda cliente *</label>
            <Autocomplete testid="acquire-company-ac"
              items={companies.map((c) => ({ id: c.id, label: c.name }))}
              initialText={acquire.company_name || ""}
              placeholder="Cerca o crea l'azienda cliente…"
              createLabel="Crea azienda"
              onSelect={(it) => setAcquire((a) => ({ ...a, company_id: it.id, company_name: it.label }))}
              onCreate={async (txt) => { try { const r = await api.post("/companies", { name: txt }); await loadCompanies(); setAcquire((a) => ({ ...a, company_id: r.data.id, company_name: r.data.name })); } catch (e) { alert("Errore: " + (e?.response?.data?.detail || e.message)); } }} />
          </div>
          <div className="modal-actions">
            <button className="btn" onClick={() => setAcquire(null)}>Annulla</button>
            <button className="btn primary" onClick={confirmAcquire} data-testid="acquire-confirm">Conferma acquisizione</button>
          </div>
        </Modal>
      )}
      {merge && (
        <Modal title="🔀 Unifica progetti doppi" onClose={() => setMerge(null)}>
          <p className="muted" style={{ fontSize: 13 }}>Cartelle, documenti, email, appunti e regole del doppione passano al progetto principale (le cartelle con lo stesso nome vengono fuse). Il doppione viene poi <b>eliminato</b>.</p>
          <div className="field"><label>Progetto principale (da tenere)</label>
            <select value={merge.primary_id} onChange={(e) => setMerge({ ...merge, primary_id: e.target.value })} data-testid="merge-primary-select">
              <option value="">— scegli —</option>
              {list.map((p) => <option key={p.id} value={p.id}>{p.name}{p.company_id ? ` · ${cname(p.company_id)}` : ""}</option>)}
            </select>
          </div>
          <div className="field"><label>Doppione (verrà eliminato)</label>
            <select value={merge.duplicate_id} onChange={(e) => setMerge({ ...merge, duplicate_id: e.target.value })} data-testid="merge-duplicate-select">
              <option value="">— scegli —</option>
              {list.filter((p) => p.id !== merge.primary_id).map((p) => <option key={p.id} value={p.id}>{p.name}{p.company_id ? ` · ${cname(p.company_id)}` : ""}</option>)}
            </select>
          </div>
          <div className="modal-actions">
            <button className="btn" onClick={() => setMerge(null)}>Annulla</button>
            <button className="btn primary" disabled={!merge.primary_id || !merge.duplicate_id || merge.busy} data-testid="merge-confirm"
              onClick={async () => {
                const pn = list.find((p) => p.id === merge.primary_id)?.name;
                const dn = list.find((p) => p.id === merge.duplicate_id)?.name;
                if (!window.confirm(`Unifico "${dn}" dentro "${pn}"?\n"${dn}" verrà eliminato.`)) return;
                setMerge({ ...merge, busy: true });
                try {
                  const r = await api.post("/projects/merge", { primary_id: merge.primary_id, duplicate_id: merge.duplicate_id });
                  alert(`Fatto! Spostati: ${r.data.documenti} documenti, ${r.data.email} email, ${r.data.appunti} appunti, ${r.data.folders_moved + r.data.folders_merged} cartelle.`);
                  setMerge(null); load();
                } catch (e) { alert("Errore: " + (e?.response?.data?.detail || e.message)); setMerge((m) => m && ({ ...m, busy: false })); }
              }}>{merge.busy ? "Unifico…" : "Unifica"}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
